import { Lock, Star as StarIcon } from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { FaShoppingBasket } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { usePagesTranslation } from "../../locales";
import UtilService from "../../../services/UtilService";
import { getJigsawTileLockReason, getJigsawTileTierRequirement, PUZZLE_TIER } from "../common/puzzleTierAccess";
import {
    formatJigsawPieceCountLabel,
    getJigsawDifficultyDisplayInfo,
    resolveJigsawTemplateTileSize,
} from "./jigsawBrowseUtils";

/** Warm amber — readable on dark dialogs; shared with the page Play control. */
export const JIGSAW_PLAY_WARM = {
    main: "#C9893A",
    dark: "#A86F2C",
    soft: "rgba(201, 137, 58, 0.22)",
    glow: "rgba(201, 137, 58, 0.45)",
};

const playContainedButtonSx = {
    background: `linear-gradient(165deg, #E0A85A 0%, ${JIGSAW_PLAY_WARM.main} 48%, ${JIGSAW_PLAY_WARM.dark} 100%)`,
    color: "#1a1208",
    fontWeight: 700,
    letterSpacing: "0.04em",
    boxShadow: `0 4px 14px ${JIGSAW_PLAY_WARM.soft}, inset 0 1px 0 rgba(255,255,255,0.28)`,
    transition: "transform 160ms ease, box-shadow 160ms ease, filter 160ms ease",
    "&:hover": {
        background: `linear-gradient(165deg, #E8B56A 0%, #D49644 48%, ${JIGSAW_PLAY_WARM.dark} 100%)`,
        boxShadow: `0 8px 22px ${JIGSAW_PLAY_WARM.glow}, inset 0 1px 0 rgba(255,255,255,0.35)`,
        filter: "brightness(1.04)",
        transform: "translateY(-1px)",
    },
    "&:active": {
        transform: "translateY(0)",
        boxShadow: `0 2px 10px ${JIGSAW_PLAY_WARM.soft}`,
    },
    "&.Mui-disabled": {
        background: "rgba(148, 163, 184, 0.2)",
        color: "rgba(226, 232, 240, 0.45)",
        boxShadow: "none",
        filter: "none",
        transform: "none",
    },
};

/** Dark-dialog readable labels (site night palette). */
const LABEL_PRIMARY = "#eef4ff";
const LABEL_MUTED = "rgba(226, 232, 240, 0.78)";
const PANEL_MUTED = LABEL_MUTED;

/** First row: Easy / Medium / Hard · Second row: Pro / Elite / Insane */
const DIFFICULTY_ROWS = [
    [6, 9, 12],
    [16, 24, 32],
];

function getDifficultyInfo(template, tileSize) {
    if (template) {
        return getJigsawDifficultyDisplayInfo(
            resolveJigsawTemplateTileSize(template),
            template?.difficultyLevel ?? template?.difficulty_level,
        );
    }
    return getJigsawDifficultyDisplayInfo(tileSize, null);
}

/**
 * Difficulty picker: two rows of size chips, then a single Play action.
 * Choosing a level only selects it; Play navigates to the board.
 */
export default function TemplateLevelGroup({
    levelEntries,
    selectedTemplate,
    setSelectedTemplate,
    storyJigsaw,
    userPlays,
    accessContext,
    onUpgrade,
    navigationState,
    /** jigsawId → color basket list (Medium+) */
    basketsByJigsawId = {},
}) {
    const { t } = usePagesTranslation();
    const navigate = useNavigate();
    const { storyId } = useParams();
    const list = levelEntries ?? [];
    if (list.length === 0) return null;

    const byTileSize = new Map(list.map((entry) => [Number(entry.tileSize), entry]));
    const selectedTileSize = selectedTemplate ? resolveJigsawTemplateTileSize(selectedTemplate) : 0;
    const isFreePuzzle = storyJigsaw?.free === true;
    const lockReason = selectedTemplate
        ? getJigsawTileLockReason(selectedTileSize, accessContext, { isFreePuzzle })
        : null;

    const startBoard = (template) => {
        if (!template?.id || !storyId) return;
        const tileSize = resolveJigsawTemplateTileSize(template);
        const reason = getJigsawTileLockReason(tileSize, accessContext, { isFreePuzzle });
        if (reason === "upgrade") {
            setSelectedTemplate(template);
            onUpgrade?.();
            return;
        }
        if (reason === "login") {
            setSelectedTemplate(template);
            navigate("/login", {
                state: { from: { pathname: `/puzzle/play-jigsaw/${storyId}` } },
            });
            return;
        }
        if (reason) {
            setSelectedTemplate(template);
            return;
        }
        navigate(`/puzzle/play-jigsaw/${storyId}/board/${template.id}`, {
            state: navigationState,
        });
    };

    const selectedBaskets =
        selectedTemplate?.id && basketsByJigsawId
            ? basketsByJigsawId[String(selectedTemplate.id)]
            : null;
    const selectedBasketCount =
        Array.isArray(selectedBaskets) && selectedBaskets.length > 0
            ? selectedBaskets.length
            : null;

    const selectedUserPlay =
        selectedTemplate && Array.isArray(userPlays)
            ? userPlays.find(
                  (p) =>
                      p.jigsaw?.jigsawTemplateId === (selectedTemplate.jigsawTemplateId ?? selectedTemplate.id)
                      || p.jigsaw?.id === selectedTemplate.id,
              )
            : null;
    const isPaused = selectedUserPlay && !selectedUserPlay.isCompleted;
    const pausedAgoStr =
        isPaused && selectedUserPlay.updatedAt
            ? UtilService.formatRelativeTime(selectedUserPlay.updatedAt)
            : null;

    const handlePlay = () => {
        if (!selectedTemplate) return;
        startBoard(selectedTemplate);
    };

    return (
        <Stack spacing={2}>
            <Stack spacing={1.25}>
                {DIFFICULTY_ROWS.map((rowSizes) => (
                    <Box
                        key={rowSizes.join("-")}
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                            gap: 1,
                        }}
                    >
                        {rowSizes.map((tileSize) => {
                            const entry = byTileSize.get(tileSize) ?? { tileSize, template: null };
                            const template = entry.template;
                            const info = getDifficultyInfo(template, tileSize);
                            const levelLabel = t(info.levelKey);
                            const gridLabel = formatJigsawPieceCountLabel(template, tileSize);
                            const isSelected = Boolean(template && selectedTemplate?.id === template.id);
                            const unavailable = !template;
                            const rowLockReason = getJigsawTileLockReason(tileSize, accessContext, {
                                isFreePuzzle,
                            });
                            const showStandardTierBadge =
                                !isFreePuzzle
                                && !accessContext?.userHasStandardPlan
                                && getJigsawTileTierRequirement(tileSize) === PUZZLE_TIER.STANDARD;

                            return (
                                <Tooltip
                                    key={`size-${tileSize}`}
                                    title={
                                        unavailable
                                            ? t("puzzle.jigsaw.size-not-linked")
                                            : `${levelLabel} · ${gridLabel}`
                                    }
                                >
                                    <Box sx={{ minWidth: 0 }}>
                                        <Button
                                            fullWidth
                                            variant={isSelected ? "contained" : "outlined"}
                                            color={info.chipColor}
                                            disabled={unavailable}
                                            onClick={() => {
                                                if (template) setSelectedTemplate(template);
                                            }}
                                            sx={{
                                                minHeight: 52,
                                                px: 0.75,
                                                py: 0.75,
                                                textTransform: "none",
                                                flexDirection: "column",
                                                gap: 0.15,
                                                borderWidth: isSelected ? 2 : 1,
                                                opacity: unavailable ? 0.55 : 1,
                                                bgcolor: isSelected
                                                    ? undefined
                                                    : "rgba(255,255,255,0.06)",
                                                "&:hover": {
                                                    bgcolor: isSelected
                                                        ? undefined
                                                        : "rgba(255,255,255,0.12)",
                                                },
                                                "&.Mui-disabled": {
                                                    borderColor: "rgba(148, 163, 184, 0.28)",
                                                    color: "rgba(148, 163, 184, 0.55)",
                                                },
                                            }}
                                        >
                                            <Typography
                                                component="span"
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 700,
                                                    lineHeight: 1.2,
                                                    color: isSelected ? "inherit" : LABEL_PRIMARY,
                                                }}
                                            >
                                                {levelLabel}
                                            </Typography>
                                            <Typography
                                                component="span"
                                                variant="caption"
                                                sx={{
                                                    lineHeight: 1.2,
                                                    opacity: isSelected ? 0.92 : 0.85,
                                                    color: isSelected ? "inherit" : LABEL_MUTED,
                                                }}
                                            >
                                                {gridLabel}
                                            </Typography>
                                            {(rowLockReason || showStandardTierBadge) && !unavailable ? (
                                                <Box
                                                    sx={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 0.35,
                                                        mt: 0.15,
                                                        color: isSelected ? "inherit" : LABEL_MUTED,
                                                    }}
                                                >
                                                    {showStandardTierBadge ? (
                                                        <StarIcon sx={{ fontSize: 14 }} aria-hidden />
                                                    ) : null}
                                                    {rowLockReason ? (
                                                        <Lock sx={{ fontSize: 14 }} aria-hidden />
                                                    ) : null}
                                                </Box>
                                            ) : null}
                                        </Button>
                                    </Box>
                                </Tooltip>
                            );
                        })}
                    </Box>
                ))}
            </Stack>

            {(selectedBasketCount != null || pausedAgoStr) && selectedTemplate ? (
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                        color: PANEL_MUTED,
                    }}
                >
                    {selectedBasketCount != null ? (
                        <Box
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.6,
                                color: "#3BA666",
                            }}
                        >
                            <FaShoppingBasket size={14} aria-hidden />
                            <Typography
                                component="span"
                                variant="caption"
                                sx={{ fontWeight: 600, color: "inherit", lineHeight: 1.2 }}
                            >
                                {t("puzzle.jigsaw.level-basket-count", {
                                    count: selectedBasketCount,
                                    defaultValue: "{{count}} baskets",
                                })}
                            </Typography>
                        </Box>
                    ) : null}
                    {pausedAgoStr ? (
                        <Typography component="span" variant="caption" sx={{ color: PANEL_MUTED }}>
                            {t("puzzle.card.paused")}: {pausedAgoStr}
                        </Typography>
                    ) : null}
                    {selectedTemplate
                        && !isFreePuzzle
                        && !accessContext?.userHasStandardPlan
                        && getJigsawTileTierRequirement(selectedTileSize) === PUZZLE_TIER.STANDARD ? (
                        <Chip
                            icon={<StarIcon />}
                            label={t("puzzle.card.premium")}
                            size="small"
                            color="warning"
                            variant="filled"
                            sx={{
                                height: 22,
                                "& .MuiChip-label": { px: 0.65, fontSize: "0.68rem", fontWeight: 600 },
                                "& .MuiChip-icon": { fontSize: "0.8rem", marginLeft: "4px", marginRight: "-2px" },
                            }}
                        />
                    ) : null}
                </Box>
            ) : null}

            <Box sx={{ pt: 0.5, display: "flex", justifyContent: "center" }}>
                {!selectedTemplate ? (
                    <Button
                        variant="contained"
                        disabled
                        sx={{ ...playContainedButtonSx, minHeight: 44, minWidth: 160, px: 4 }}
                    >
                        {t("puzzle.card.play")}
                    </Button>
                ) : lockReason === "upgrade" ? (
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<Lock />}
                        onClick={() => onUpgrade?.()}
                        sx={{ minHeight: 44, minWidth: 160, px: 4 }}
                    >
                        {t("puzzle.card.upgrade-to-play")}
                    </Button>
                ) : lockReason === "login" ? (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<Lock />}
                        onClick={() => {
                            if (storyId) {
                                navigate("/login", {
                                    state: { from: { pathname: `/puzzle/play-jigsaw/${storyId}` } },
                                });
                            }
                        }}
                        sx={{ minHeight: 44, minWidth: 160, px: 4 }}
                    >
                        {t("puzzle.card.sign-in-to-play")}
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={handlePlay}
                        sx={{ ...playContainedButtonSx, minHeight: 44, minWidth: 160, px: 4 }}
                    >
                        {t("puzzle.card.play")}
                    </Button>
                )}
            </Box>
        </Stack>
    );
}
